import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  RateReview as RateReviewIcon,
  Place as PlaceIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import FeedLayout from '../../components/feed/FeedLayout';

const reportTabs = [
  { label: 'Location Performance', icon: <AssessmentIcon /> },
  { label: 'Review Analytics', icon: <RateReviewIcon /> },
  { label: 'Regional Summary', icon: <PlaceIcon /> },
  { label: 'Custom Reports', icon: <BuildIcon /> },
];

const locationPerformanceReports = [
  {
    title: 'Individual Location Report',
    description: 'Complete metrics for a single location including reviews, ratings, posts, and engagement.',
    available: true,
  },
  {
    title: 'Multi-Location Comparison',
    description: 'Compare performance across multiple locations with side-by-side metrics and rankings.',
    available: true,
  },
  {
    title: 'Top Performers Report',
    description: 'Identify your highest-rated and most-engaged locations with key success metrics.',
    available: true,
  },
  {
    title: 'Underperforming Locations',
    description: 'Flag locations that need attention with declining ratings or low engagement.',
    available: true,
  },
];

const reviewAnalyticsReports = [
  {
    title: 'Review Velocity Report',
    description: 'Track review growth over time with trends and velocity scoring.',
    available: true,
  },
  {
    title: 'Rating Distribution Analysis',
    description: 'Analyze star rating patterns across your portfolio.',
    available: true,
  },
  {
    title: 'Response Rate Report',
    description: 'Measure owner response rates and response time metrics.',
    available: true,
  },
  {
    title: 'Sentiment Analysis',
    description: 'AI-powered sentiment insights from review text.',
    available: false,
    comingSoon: true,
  },
];

const regionalSummaryReports = [
  {
    title: 'Regional Performance Comparison',
    description: 'Compare metrics across all regions with rankings and trends.',
    available: true,
  },
  {
    title: 'Regional Growth Report',
    description: 'Track location and review growth by region over time.',
    available: true,
  },
  {
    title: 'Market Penetration Analysis',
    description: 'Analyze location density and market coverage by region.',
    available: true,
  },
];

const ReportCard = ({ report, onGenerate }) => (
  <Card
    sx={{
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': {
        borderColor: report.available ? '#3b5998' : 'divider',
        boxShadow: report.available ? '0 4px 12px rgba(59, 89, 152, 0.15)' : 'none',
      },
      transition: 'all 0.2s ease',
      opacity: report.available ? 1 : 0.7,
    }}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a2b4a' }}>
            {report.title}
          </Typography>
          {report.comingSoon && (
            <Chip
              label="Coming Soon"
              size="small"
              sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {report.description}
        </Typography>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          fullWidth
          disabled={!report.available}
          onClick={onGenerate}
          sx={{
            color: '#3b5998',
            borderColor: '#3b5998',
            '&:hover': {
              bgcolor: 'rgba(59, 89, 152, 0.04)',
              borderColor: '#2d4373',
            },
            '&.Mui-disabled': {
              borderColor: 'divider',
            },
          }}
        >
          Generate Report
        </Button>
      </Box>
    </CardContent>
  </Card>
);

const CustomReportsTab = () => (
  <Box>
    <Card sx={{ border: '1px solid', borderColor: 'divider', p: 4, textAlign: 'center' }}>
      <BuildIcon sx={{ fontSize: 64, color: 'rgba(59, 89, 152, 0.3)', mb: 2 }} />
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a2b4a', mb: 1 }}>
        Custom Report Builder
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
        Build custom reports with the metrics that matter most to your business.
        Select regions, date ranges, and specific KPIs to create tailored insights.
      </Typography>
      <Chip
        label="Coming Soon"
        sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', fontSize: '0.875rem', py: 0.5 }}
      />
    </Card>
  </Box>
);

const FeedReports = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleGenerateReport = (reportTitle) => {
    alert(`Generating report: ${reportTitle}\n\nThis feature will be available when the API is connected.`);
  };

  const getReportsForTab = () => {
    switch (activeTab) {
      case 0:
        return locationPerformanceReports;
      case 1:
        return reviewAnalyticsReports;
      case 2:
        return regionalSummaryReports;
      default:
        return [];
    }
  };

  return (
    <FeedLayout
      title="Reports"
      subtitle="Export and analyze performance data across your portfolio"
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: '#3b5998',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b5998',
            },
          }}
        >
          {reportTabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Box>

      {/* Tab Description */}
      <Box sx={{ mb: 3 }}>
        <Chip
          label="Reports"
          sx={{
            bgcolor: '#3b5998',
            color: 'white',
            fontWeight: 500,
            mr: 2,
          }}
        />
        <Typography variant="body2" color="text.secondary" component="span">
          {activeTab === 0 && 'Export detailed performance metrics for individual locations or groups of locations.'}
          {activeTab === 1 && 'Analyze review trends, ratings, and customer sentiment across your portfolio.'}
          {activeTab === 2 && 'Compare performance across regions and identify market opportunities.'}
          {activeTab === 3 && 'Build custom reports with your preferred metrics and filters.'}
        </Typography>
      </Box>

      {/* Report Cards */}
      {activeTab === 3 ? (
        <CustomReportsTab />
      ) : (
        <Grid container spacing={3}>
          {getReportsForTab().map((report, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <ReportCard
                report={report}
                onGenerate={() => handleGenerateReport(report.title)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Export Formats Note */}
      {activeTab !== 3 && (
        <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(59, 89, 152, 0.04)', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a2b4a', mb: 1 }}>
            Export Formats
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All reports can be exported in PDF, Excel (XLSX), or CSV formats. Select your preferred
            format when generating the report.
          </Typography>
        </Box>
      )}
    </FeedLayout>
  );
};

export default FeedReports;
